import { useEffect, useRef } from 'react';
import * as api from '../api/client';
import type { AgentTimelineEvent } from '../agentTimeline/normalize';
import {
  normalizeMosooThreadEvent,
  normalizeMosooThreadFile,
} from '../agentTimeline/mosoo';

type ChatAttachment = { r2_key: string; mime: string; name: string; size: number };

export interface ChatEvent {
  type: string;
  text?: string;
  stream?: string;
  agent?: string;
  message?: string;
  session_id?: string;
  exit_code?: number;
  duration_seconds?: number;
  reason?: string;
  [key: string]: unknown;
}

export interface ActivityPayload {
  phase: string;
  tool: string;
  content: string;
  agent: string;
  agentId: string;
  turnId: string;
  ts: number;
}

export interface DonePayload {
  agent: string;
  agentId: string;
  turnId: string;
  durationMs: number;
  summary: string;
  ts: number;
}

export interface DeckTimelineHandlers {
  onTimelineEvent?: (event: AgentTimelineEvent) => void;
  onActivity?: (a: ActivityPayload) => void;
  onActivityDone?: (d: DonePayload) => void;
  onAgentStatus?: (status: string, tulpaName?: string) => void;
  onAgentProgramStatus?: (programStatus: string, tulpaName?: string) => void;
  onArtifactSynced?: (phase: string, artifactKey: string, data: unknown) => void;
  onConnected?: (isReconnect: boolean) => void;
  onContent?: (text: string, agent: string, turnId: string, agentId: string) => void;
  onContextUpdated?: (action: string, key: string, actor: string) => void;
  onDeckUpdated?: (fields: string[]) => void;
  onDone?: (event: ChatEvent) => void;
  onError?: (message: string, agent: string) => void;
  onHistoryLoaded?: () => void;
  onPhaseChanged?: (from: string, to: string) => void;
  onSessionLimit?: () => void;
  onSlideProgress?: (slide: number, total: number, deckFolder: string) => void;
  onStreamDropped?: (consecutiveErrors: number) => void;
  onTaskStatus?: (task: string) => void;
  onUserMessage?: (
    messageId: string,
    content: string,
    files?: string[],
    attachments?: ChatAttachment[],
  ) => void;
}

function emitTimelineEvent(
  event: AgentTimelineEvent,
  handlers: DeckTimelineHandlers,
  currentTurnIdRef: { current: string },
): void {
  if (event.turnId) currentTurnIdRef.current = event.turnId;
  handlers.onTimelineEvent?.(event);

  switch (event.type) {
    case 'message.user':
      handlers.onUserMessage?.(event.messageId, event.content, [], event.attachments);
      break;
    case 'message.assistant.delta':
      handlers.onContent?.(event.delta, event.agent, event.turnId, event.agentId);
      break;
    case 'action':
      handlers.onActivity?.({
        phase: event.phase,
        tool: event.tool,
        content: event.content,
        agent: event.agent,
        agentId: event.agentId,
        turnId: event.turnId,
        ts: event.ts,
      });
      break;
    case 'turn.completed':
      handlers.onDone?.({ type: 'done', turn_id: event.turnId, agent: event.agent });
      handlers.onActivityDone?.({
        agent: event.agent,
        agentId: event.agentId,
        turnId: event.turnId,
        durationMs: event.durationMs,
        summary: event.summary,
        ts: event.ts,
      });
      break;
    case 'error':
      handlers.onError?.(event.message, event.agent);
      break;
  }
}

export function useDeckTimeline(
  deckId: string | null,
  handlers: DeckTimelineHandlers,
  reconnectKey = 0,
) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    if (!deckId) return;
    const activeDeckId = deckId;
    const seenIds = new Set<string>();
    const currentTurnIdRef = { current: '' };
    let cancelled = false;
    let connectCount = 0;
    let consecutiveErrors = 0;
    let stream: EventSource | null = null;

    const emitMosooEvent = (event: api.MosooThreadEvent, agentId: string) => {
      const key = `mosoo:${event.id}`;
      if (seenIds.has(key)) return;
      seenIds.add(key);
      for (const normalized of normalizeMosooThreadEvent(event, agentId)) {
        emitTimelineEvent(normalized, handlersRef.current, currentTurnIdRef);
      }
    };

    const loadFiles = async (agentId: string) => {
      const result = await api.listDeckMosooThreadFiles(activeDeckId);
      for (const file of result.result.files) {
        if (cancelled) return;
        const key = `mosoo-file:${file.id}`;
        if (seenIds.has(key)) continue;
        const normalized = normalizeMosooThreadFile(
          file,
          agentId,
          api.deckMosooThreadFileContentUrl(activeDeckId, file.id),
        );
        if (!normalized) continue;
        seenIds.add(key);
        emitTimelineEvent(normalized, handlersRef.current, currentTurnIdRef);
      }
    };

    void (async () => {
      try {
        const binding = await api.ensureDeckMosooThread(activeDeckId);
        if (!binding) throw new Error('Mosoo Thread is required for this deck.');
        const [eventResult] = await Promise.all([
          api.listDeckMosooThreadEvents(activeDeckId, 400),
          loadFiles(binding.mosoo_agent_id),
        ]);
        if (cancelled) return;
        for (const event of eventResult.result.events) {
          emitMosooEvent(event, binding.mosoo_agent_id);
        }
        handlersRef.current.onHistoryLoaded?.();

        stream = new EventSource(api.deckMosooThreadEventStreamUrl(activeDeckId, 400), {
          withCredentials: true,
        });
        stream.onopen = () => {
          connectCount += 1;
          consecutiveErrors = 0;
          handlersRef.current.onConnected?.(connectCount > 1);
        };
        stream.addEventListener('thread.event', (raw) => {
          try {
            const event = JSON.parse((raw as MessageEvent).data) as api.MosooThreadEvent;
            emitMosooEvent(event, binding.mosoo_agent_id);
            if (event.type === 'session_files.updated' || event.type === 'run.completed') {
              void loadFiles(binding.mosoo_agent_id).catch(() => {});
            }
          } catch {
            /* Ignore malformed upstream events. */
          }
        });
        stream.addEventListener('thread.error', (raw) => {
          try {
            const body = JSON.parse((raw as MessageEvent).data) as { error?: { message?: string } };
            handlersRef.current.onError?.(
              body.error?.message || 'Mosoo event stream failed.',
              'PitchPilot',
            );
          } catch {
            handlersRef.current.onError?.('Mosoo event stream failed.', 'PitchPilot');
          }
        });
        stream.onerror = () => {
          if (cancelled) return;
          consecutiveErrors += 1;
          handlersRef.current.onStreamDropped?.(consecutiveErrors);
        };
      } catch (error) {
        if (cancelled) return;
        handlersRef.current.onHistoryLoaded?.();
        handlersRef.current.onError?.(
          error instanceof Error ? error.message : String(error),
          'PitchPilot',
        );
      }
    })();

    return () => {
      cancelled = true;
      stream?.close();
    };
  }, [deckId, reconnectKey]);
}
